import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AttributeDefinition, CatalogService } from './catalog.service';

interface AttributeFieldSnapshot {
  textValue: string;
  booleanValue: boolean;
  hasValue: boolean;
}

interface AttributeFieldModel {
  definition: AttributeDefinition;
  textValue: string;
  booleanValue: boolean;
  hasValue: boolean;
  removed: boolean;
  removedSnapshot?: AttributeFieldSnapshot;
}

@Component({
  standalone: true,
  selector: 'ain-catalog-offering-attributes',
  imports: [
    FormsModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    SelectModule,
    InfoHintComponent
  ],
  template: `
    <section class="attributes-manager">
      <ain-info-hint
        [titleKey]="'catalog.offerings.attributes.hint.title'"
        [descriptionKey]="'catalog.offerings.attributes.hint.description'"
        [bulletKeys]="[
          'catalog.offerings.attributes.hint.bullets.source',
          'catalog.offerings.attributes.hint.bullets.capabilities',
          'catalog.offerings.attributes.hint.bullets.save'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" [disabled]="loading() || saving()" (click)="load()"></button>
        <button pButton type="button" [label]="t('catalog.offerings.attributes.save')" [disabled]="loading() || saving() || !offeringId()" (click)="save()"></button>
      </div>

      @if (loading()) {
        <p class="muted">{{ t('common.loading') }}</p>
      } @else if (fields().length === 0) {
        <p class="muted">{{ t('catalog.offerings.attributes.noDefinitions') }}</p>
      } @else {
        @if (capabilityFields().length > 0) {
          <section class="attribute-group">
            <h4>{{ t('catalog.offerings.attributes.capabilitiesSection') }}</h4>
            <div class="capability-grid">
              @for (field of capabilityFields(); track field.definition.id) {
                <div class="capability-item" [class.field-removed]="field.removed">
                  <label class="capability-label">
                    <p-checkbox [binary]="true" [(ngModel)]="field.booleanValue" [disabled]="field.removed || saving()" />
                    <span>{{ field.definition.displayName || field.definition.key }}</span>
                  </label>
                  <div class="field-actions">
                    @if (field.removed) {
                      <button
                        pButton
                        type="button"
                        severity="secondary"
                        text
                        icon="pi pi-undo"
                        [attr.aria-label]="t('catalog.offerings.attributes.undoRemove')"
                        [disabled]="saving()"
                        (click)="undoRemoveAssignment(field.definition.id)"></button>
                    } @else {
                      <button
                        pButton
                        type="button"
                        severity="danger"
                        text
                        icon="pi pi-times"
                        [attr.aria-label]="t('catalog.offerings.attributes.removeAssignment')"
                        [disabled]="saving() || !canRemoveAssignment(field)"
                        (click)="removeAssignment(field.definition.id)"></button>
                    }
                  </div>
                </div>
              }
            </div>
          </section>
        }

        @if (metadataFields().length > 0) {
          <section class="attribute-group">
            <h4>{{ t('catalog.offerings.attributes.metadataSection') }}</h4>
            <div class="metadata-grid">
              @for (field of metadataFields(); track field.definition.id) {
                <div class="metadata-row" [class.field-removed]="field.removed">
                  <label class="metadata-label">
                    <span>{{ field.definition.displayName || field.definition.key }}</span>
                    @if (isBoolean(field.definition)) {
                      <div class="bool-row">
                        <p-checkbox [binary]="true" [(ngModel)]="field.booleanValue" [disabled]="field.removed || saving()" />
                      </div>
                    } @else {
                      <input pInputText [(ngModel)]="field.textValue" [disabled]="field.removed || saving()" />
                    }
                  </label>
                  <div class="field-actions">
                    @if (field.removed) {
                      <button
                        pButton
                        type="button"
                        severity="secondary"
                        text
                        icon="pi pi-undo"
                        [attr.aria-label]="t('catalog.offerings.attributes.undoRemove')"
                        [disabled]="saving()"
                        (click)="undoRemoveAssignment(field.definition.id)"></button>
                    } @else {
                      <button
                        pButton
                        type="button"
                        severity="danger"
                        text
                        icon="pi pi-times"
                        [attr.aria-label]="t('catalog.offerings.attributes.removeAssignment')"
                        [disabled]="saving() || !canRemoveAssignment(field)"
                        (click)="removeAssignment(field.definition.id)"></button>
                    }
                  </div>
                </div>
              }
            </div>
          </section>
        }
      }

      <details class="definition-panel">
        <summary>{{ t('catalog.offerings.attributes.addDefinition') }}</summary>
        <div class="definition-form">
          <label>
            <span>{{ t('catalog.offerings.attributes.key') }}</span>
            <input pInputText [(ngModel)]="newDefinition.key" [placeholder]="t('catalog.offerings.attributes.keyPlaceholder')" />
          </label>
          <label>
            <span>{{ t('catalog.offerings.attributes.displayName') }}</span>
            <input pInputText [(ngModel)]="newDefinition.displayName" />
          </label>
          <label>
            <span>{{ t('catalog.offerings.attributes.type') }}</span>
            <p-select
              [(ngModel)]="newDefinition.type"
              [options]="typeOptions"
              optionLabel="label"
              optionValue="value"
              appendTo="body" />
          </label>
          <button
            pButton
            type="button"
            severity="secondary"
            [label]="t('catalog.offerings.attributes.createDefinition')"
            [disabled]="creatingDefinition() || !newDefinition.key.trim() || !newDefinition.displayName.trim()"
            (click)="createDefinition()"></button>
        </div>
      </details>

      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
      @if (message()) {
        <p class="success">{{ message() }}</p>
      }
    </section>
  `,
  styles: [`
    .attributes-manager { display: grid; gap: 12px; margin-top: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .attribute-group { display: grid; gap: 10px; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; }
    .attribute-group h4 { margin: 0; font-size: 14px; }
    .capability-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
    .capability-item { display: flex; align-items: center; gap: 8px; border: 1px solid transparent; border-radius: 8px; padding: 4px 6px; }
    .capability-label { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; cursor: pointer; }
    .metadata-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; }
    .metadata-row { display: flex; align-items: flex-start; gap: 6px; border: 1px solid transparent; border-radius: 8px; padding: 4px 6px; }
    .metadata-label { display: grid; gap: 6px; flex: 1; min-width: 0; }
    .metadata-label span { color: #475569; font-size: 12px; font-weight: 600; }
    .field-actions { display: flex; align-items: center; flex-shrink: 0; }
    .field-removed { opacity: 0.55; background: #f8fafc; border-color: #e2e8f0; }
    .field-removed .capability-label span,
    .field-removed .metadata-label span { text-decoration: line-through; color: #94a3b8; }
    .bool-row { min-height: 2.5rem; display: flex; align-items: center; }
    .definition-panel { border: 1px dashed #cbd5e1; border-radius: 10px; padding: 10px 12px; }
    .definition-panel summary { cursor: pointer; font-weight: 600; color: #334155; }
    .definition-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin-top: 10px; align-items: end; }
    .definition-form label { display: grid; gap: 6px; }
    .definition-form span { color: #475569; font-size: 12px; font-weight: 600; }
    .muted { margin: 0; color: #64748b; }
    .error { margin: 0; color: #b91c1c; }
    .success { margin: 0; color: #166534; }
  `]
})
export class CatalogOfferingAttributesComponent {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly offeringId = input.required<string>();

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly creatingDefinition = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly fields = signal<AttributeFieldModel[]>([]);

  readonly capabilityFields = computed(() =>
    this.fields().filter(field => field.definition.key.startsWith('capability_')));

  readonly metadataFields = computed(() =>
    this.fields().filter(field => !field.definition.key.startsWith('capability_')));

  readonly typeOptions = [
    { value: 'Boolean', label: 'Boolean' },
    { value: 'Text', label: 'Text' }
  ];

  newDefinition = {
    key: '',
    displayName: '',
    type: 'Boolean'
  };

  constructor() {
    effect(() => {
      const id = this.offeringId();
      if (id) {
        this.load();
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  isBoolean(definition: AttributeDefinition): boolean {
    return definition.type.toLowerCase() === 'boolean';
  }

  canRemoveAssignment(field: AttributeFieldModel): boolean {
    if (field.removed) {
      return false;
    }

    if (field.hasValue) {
      return true;
    }

    if (this.isBoolean(field.definition)) {
      return field.booleanValue;
    }

    return field.textValue.trim().length > 0;
  }

  removeAssignment(definitionId: string): void {
    this.message.set('');
    this.fields.update(current =>
      current.map(field => {
        if (field.definition.id !== definitionId || field.removed) {
          return field;
        }

        return {
          ...field,
          removed: true,
          removedSnapshot: {
            textValue: field.textValue,
            booleanValue: field.booleanValue,
            hasValue: field.hasValue
          },
          textValue: '',
          booleanValue: false,
          hasValue: false
        };
      }));
  }

  undoRemoveAssignment(definitionId: string): void {
    this.message.set('');
    this.fields.update(current =>
      current.map(field => {
        if (field.definition.id !== definitionId || !field.removed || !field.removedSnapshot) {
          return field;
        }

        return {
          ...field,
          removed: false,
          textValue: field.removedSnapshot.textValue,
          booleanValue: field.removedSnapshot.booleanValue,
          hasValue: field.removedSnapshot.hasValue,
          removedSnapshot: undefined
        };
      }));
  }

  load(): void {
    const id = this.offeringId();
    if (!id) {
      return;
    }

    this.loading.set(true);
    this.error.set('');
    this.message.set('');

    this.service.listAttributeDefinitions().subscribe({
      next: definitions => {
        this.service.getOfferingAttributes(id).subscribe({
          next: assignments => {
            const valuesByDefinitionId = new Map(
              assignments.map(item => [item.attributeDefinitionId, item.value]));
            this.fields.set(
              definitions
                .slice()
                .sort((left, right) => left.key.localeCompare(right.key))
                .map(definition => this.toFieldModel(definition, valuesByDefinitionId.get(definition.id) ?? '')));
            this.loading.set(false);
          },
          error: () => {
            this.fields.set([]);
            this.loading.set(false);
            this.error.set(this.t('catalog.offerings.attributes.loadFailed'));
          }
        });
      },
      error: () => {
        this.fields.set([]);
        this.loading.set(false);
        this.error.set(this.t('catalog.offerings.attributes.loadFailed'));
      }
    });
  }

  save(): void {
    const id = this.offeringId();
    if (!id) {
      return;
    }

    this.saving.set(true);
    this.error.set('');
    this.message.set('');

    const values = this.fields()
      .map(field => ({
        field,
        value: this.serializeFieldValue(field)
      }))
      .filter(({ field, value }) => {
        if (field.removed) {
          return false;
        }

        if (this.isBoolean(field.definition)) {
          return field.booleanValue || field.hasValue;
        }

        return value.trim().length > 0;
      })
      .map(({ field, value }) => ({
        attributeDefinitionId: field.definition.id,
        value
      }));

    this.service.updateOfferingAttributes(id, values).subscribe({
      next: saved => {
        const valuesByDefinitionId = new Map(
          saved.map(item => [item.attributeDefinitionId, item.value]));
        this.fields.update(current =>
          current.map(field => {
            const value = valuesByDefinitionId.get(field.definition.id) ?? '';
            return this.toFieldModel(field.definition, value);
          }));
        this.saving.set(false);
        this.message.set(this.t('catalog.offerings.attributes.saveSuccess'));
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.offerings.attributes.saveFailed'));
      }
    });
  }

  createDefinition(): void {
    const key = this.newDefinition.key.trim();
    const displayName = this.newDefinition.displayName.trim();
    if (!key || !displayName) {
      return;
    }

    this.creatingDefinition.set(true);
    this.error.set('');
    this.service.createAttributeDefinition({
      key,
      displayName,
      type: this.newDefinition.type,
      isFilterable: key.startsWith('capability_'),
      isSearchable: false
    }).subscribe({
      next: created => {
        this.fields.update(current => [
          ...current,
          this.toFieldModel(created, '')
        ].sort((left, right) => left.definition.key.localeCompare(right.definition.key)));
        this.newDefinition = { key: '', displayName: '', type: 'Boolean' };
        this.creatingDefinition.set(false);
        this.message.set(this.t('catalog.offerings.attributes.definitionCreated'));
      },
      error: () => {
        this.creatingDefinition.set(false);
        this.error.set(this.t('catalog.offerings.attributes.definitionCreateFailed'));
      }
    });
  }

  private toFieldModel(definition: AttributeDefinition, rawValue: string): AttributeFieldModel {
    const trimmed = rawValue.trim();
    const booleanValue = this.parseBoolean(trimmed);
    return {
      definition,
      textValue: this.isBoolean(definition) ? '' : trimmed,
      booleanValue,
      hasValue: trimmed.length > 0,
      removed: false
    };
  }

  private serializeFieldValue(field: AttributeFieldModel): string {
    if (this.isBoolean(field.definition)) {
      return field.booleanValue ? 'true' : 'false';
    }

    return field.textValue.trim();
  }

  private parseBoolean(value: string): boolean {
    if (!value) {
      return false;
    }

    return value.toLowerCase() === 'true' || value === '1';
  }
}
